import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { ErrorNote } from "../components/Feedback";
import { Btn, Card, ConfirmDialog, Field, Input, Textarea } from "../components/Shared";

// The project settings card and Delete project (task 2026-10-12 "API-only operations get
// screens"). Settings, for whoever may manage the project (`can_manage`): the fields of
// PATCH /projects/{id} (the server's refusal is shown as it sends it). The members are a card of
// their own (ProjectMembers.jsx). A new project field is one entry in PATCH_FIELDS and one
// <Field> below. Delete project: portal admins only (the route's rule), the slug typed again,
// sent as ?confirm=<slug> — what the backend asks for a hosted project, one with releases or one
// with nightly backups.

const PATCH_FIELDS = ["name", "description"];
const text = v => v ?? "";

export function ProjectSettings({ project, onSaved, onClose }) {
  const [form, setForm] = useState(() => Object.fromEntries(PATCH_FIELDS.map(k => [k, text(project[k])])));
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    // Only what changed: an empty description is sent as null, as a new project has it.
    const patch = Object.fromEntries(PATCH_FIELDS.filter(k => form[k] !== text(project[k]))
      .map(k => [k, k === "name" ? form[k] : (form[k] || null)]));
    try {
      if (Object.keys(patch).length) await api.patch(`/projects/${project.id}`, patch);
      setNotice(Object.keys(patch).length ? "Settings saved." : "Nothing changed.");
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <form onSubmit={submit} aria-label="Project settings" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Project settings</h2>
        <ErrorNote error={error} />
        {notice && <p role="status" style={{ margin: 0, fontSize: 14, color: "var(--dp-accent-dark)" }}>{notice}</p>}
        <Field label="Name" required><Input value={form.name} onChange={set("name")} /></Field>
        <Field label="Description"><Textarea value={form.description} onChange={set("description")} /></Field>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={busy || !form.name.trim()}>Save settings</Btn>
          <Btn variant="secondary" onClick={onClose}>Close</Btn>
        </div>
      </form>
    </Card>
  );
}

export function DeleteProjectDialog({ project, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/projects/${project.id}?confirm=${encodeURIComponent(project.slug)}`);
      navigate("/projects");
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog title="Delete project" confirmWord={project.slug} confirmLabel="Delete project" busy={busy}
                   onConfirm={remove} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        Delete <strong>{project.name}</strong> with every study, dataset and file in it, its
        database design versions and its built database
        {project.hosted_version != null && <> (<strong>hosted: every row entered into it and every file uploaded into it are deleted</strong>)</>},
        and its nightly backups; its published releases stay in the release archive. This cannot be undone.
      </p>
      <div style={{ marginTop: 12 }}><ErrorNote error={error} /></div>
    </ConfirmDialog>
  );
}
